import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import api from "../api";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [resetPasswordMessage, setResetPasswordMessage] = useState("");

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openResetDialog, setOpenResetDialog] = useState(false);

  const [editForm, setEditForm] = useState({
    responsavel: "",
    email: "",
    telefone: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get("/usuarios");
      const sortedUsers = response.data.sort((a, b) => {
        const ufA = a.nome.slice(-2);
        const ufB = b.nome.slice(-2);
        return ufA.localeCompare(ufB);
      });
      setUsers(sortedUsers);
    } catch (error) {
      setErrorMessage("Erro ao carregar os usuários.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditForm({
      responsavel: user.responsavel || "",
      email: user.email || "",
      telefone: user.telefone || "",
    });
    setOpenEditDialog(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser) return;

    try {
      await api.put(`/usuarios/${selectedUser.id}`, editForm);
      setSuccessMessage("Usuário atualizado com sucesso.");
      setOpenEditDialog(false);
      fetchUsers();
    } catch (error) {
      setErrorMessage("Erro ao atualizar o usuário.");
    }
  };

  const handleOpenResetDialog = (user) => {
    setSelectedUser(user);
    setOpenResetDialog(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    setResetPasswordMessage("");
    try {
      const response = await api.put(
        `/usuarios/${selectedUser.id}/resetar-senha`
      );
      setResetPasswordMessage(`Nova senha para o usuário é: ${response.data}`);
      setOpenResetDialog(false);
    } catch (error) {
      setErrorMessage("Erro ao resetar a senha.");
    }
  };

  const handleCloseDialogs = () => {
    setOpenEditDialog(false);
    setOpenResetDialog(false);
    setSelectedUser(null);
  };

  const handlePhoneChange = (value) => {
    // Remove caracteres não numéricos
    const onlyNumbers = value.replace(/\D/g, "");
    // Aplica a máscara ao número de telefone no formato (99) 999999999
    const formattedPhone = onlyNumbers
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .slice(0, 14);
    setEditForm({ ...editForm, telefone: formattedPhone });
  };

  return (
    <Box sx={{ maxWidth: "1000px", margin: "0 auto", padding: 4 }}>
      <Typography variant="h4" mb={4}>
        Gestão de Usuários
      </Typography>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {resetPasswordMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {resetPasswordMessage}
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <TableContainer>
          <Table>
            <TableHead className="table-header">
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Responsável</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.nome}</TableCell>
                  <TableCell>{user.responsavel || "-"}</TableCell>
                  <TableCell>{user.email || "-"}</TableCell>
                  <TableCell>{user.telefone || "-"}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleEditUser(user)}
                      sx={{ mr: 1 }}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => handleOpenResetDialog(user)}
                    >
                      Resetar Senha
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog para edição */}
      <Dialog open={openEditDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Editar Usuário</DialogTitle>
        <DialogContent>
          <TextField
            label="Responsável"
            value={editForm.responsavel}
            onChange={(e) =>
              setEditForm({ ...editForm, responsavel: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Email"
            type="email"
            value={editForm.email}
            onChange={(e) =>
              setEditForm({ ...editForm, email: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Telefone"
            value={editForm.telefone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            Cancelar
          </Button>
          <Button onClick={handleSaveUser} color="primary" variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para resetar senha */}
      <Dialog open={openResetDialog} onClose={handleCloseDialogs}>
        <DialogTitle>Resetar Senha</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja resetar a senha de{" "}
            <strong>{selectedUser?.nome}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs} color="primary">
            Não
          </Button>
          <Button
            onClick={handleResetPassword}
            color="secondary"
            variant="contained"
          >
            Sim
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
